import { Injectable } from '@angular/core';
import { ApplicationContext } from '../models/application-context.model';
import { Observable, Subject, catchError, map, of, switchMap } from 'rxjs';
import { UserService } from 'src/app/core/services/user.service';
import { EditableCompanyInfo, User, UserCompany } from 'src/app/pages/users/user.model';
import { CompanyService } from './company.service';
import { CompanyProductLicense } from '../models/company.model';
import { merge } from 'lodash';
import { AdminCompanyAutoSelectService } from './admin-company-auto-select.service';
import { AdminScopeService } from './admin-scope.service';
import {
  getVisibleUserCompanies,
  resolveAdminOrganizationContext,
} from '../utils/admin-company.util';

@Injectable({
  providedIn: 'root'
})
export class ApplicationContextService {
  private _applicationContext: ApplicationContext;
  private _contextChangedSubject: Subject<ApplicationContext> = new Subject<ApplicationContext>();

  constructor(
    private readonly _userService: UserService,
    private readonly _companyService: CompanyService,
    private readonly _adminCompanyAutoSelect: AdminCompanyAutoSelectService,
    private readonly _adminScope: AdminScopeService,
  ) { }

  get context(): Observable<ApplicationContext> {
    if (this._applicationContext) {
      return of(this._applicationContext);
    }
    return this.initializeApplicationContext().pipe(map(context => {
      this._applicationContext = context;
      this._contextChangedSubject.next(context);
      return context;
    }));
  }

  get contextChanged$(): Subject<ApplicationContext> {
    return this._contextChangedSubject;
  }

  updateCompanyInformation(companyId: string, updatedCompanyInfo: EditableCompanyInfo) {
    this._applicationContext.currentlyLoggedInUser.companyName = updatedCompanyInfo.name;
    const matchingCompany = this._applicationContext.userCompanies.find(c => c.companyId === companyId);
    if (!matchingCompany) {
      return;
    }
    merge(matchingCompany.company, updatedCompanyInfo);
    this._contextChangedSubject.next(this._applicationContext);
  }

  resetContext() {
    this._applicationContext = null;
  }

  private initializeApplicationContext = (): Observable<ApplicationContext> => {
    return this._userService.getLoggedInUser().pipe(
      catchError(() => of(null)),
      switchMap((user: User | null) => {
        if (!user?.id) {
          return of({
            currentlyLoggedInUser: null,
            userCompanies: [],
            companyLicenses: [],
          } as ApplicationContext);
        }
        return this._userService.getUserCompanies(user.id).pipe(
          catchError(() => of([] as UserCompany[])),
          switchMap((userCompanies: UserCompany[]) => {
            // All-orgs mode has no X-Organization-Id — skip company-scoped licenses.
            const skipLicenses =
              this._adminScope.isAllOrganizations && !!user.superGlobalAdmin;
            const licenses$ = skipLicenses
              ? of([] as CompanyProductLicense[])
              : this._companyService.getCompanyProductsLicenses().pipe(
                  catchError(() => of([] as CompanyProductLicense[]))
                );
            return licenses$.pipe(
              map((licenses: CompanyProductLicense[]) => ({
                currentlyLoggedInUser: user,
                userCompanies,
                companyLicenses: licenses.map((l) => l.name),
              }))
            );
          })
        );
      }),
      switchMap((context: ApplicationContext) => this.maybeAutoSelectOrganization(context))
    );
  }

  /** Page refresh: only auto-scope for single-org users missing org context. */
  private maybeAutoSelectOrganization(
    context: ApplicationContext
  ): Observable<ApplicationContext> {
    const needsOrg =
      resolveAdminOrganizationContext({
        companyId: context.currentlyLoggedInUser?.companyId,
        userCompanies: context.userCompanies,
        superGlobalAdmin: !!context.currentlyLoggedInUser?.superGlobalAdmin,
        allOrganizationsScope:
          this._adminScope.isAllOrganizations &&
          !!context.currentlyLoggedInUser?.superGlobalAdmin,
      }) === 'select-company';

    if (!needsOrg || getVisibleUserCompanies(context.userCompanies).length !== 1) {
      return of(context);
    }

    return this._adminCompanyAutoSelect
      .tryAutoSelectSingleCompany(context, { navigate: false })
      .pipe(
        switchMap((selected) =>
          selected ? this.initializeApplicationContext() : of(context)
        )
      );
  }
}
